import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Animates elements with a `data-count` attribute from 0 to their numeric
 * target when they intersect the viewport. Mirrors the original script.js
 * behaviour (easeOutCubic over 1200ms, preserves trailing suffix like %, x).
 */
export function useCounters() {
  const { pathname } = useLocation();

  useEffect(() => {
    const counters = document.querySelectorAll<HTMLElement>("[data-count]");
    if (!counters.length) return;

    const fmt = (n: number) =>
      n % 1 !== 0
        ? n.toFixed(1).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        : Math.round(n)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
    const duration = 1200;

    const animate = (el: HTMLElement) => {
      const raw = el.getAttribute("data-count");
      if (!raw) return;
      const match = raw.match(/^([\d.]+)(.*)/);
      if (!match) return;
      const target = parseFloat(match[1]);
      const suffix = match[2] || "";
      let start: number | null = null;

      const step = (ts: number) => {
        if (start === null) start = ts;
        const progress = Math.min((ts - start) / duration, 1);
        const val = target * easeOutCubic(progress);
        el.textContent = fmt(val) + suffix;
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            animate(e.target as HTMLElement);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.5 }
    );
    counters.forEach((el) => obs.observe(el));

    return () => obs.disconnect();
  }, [pathname]);
}
